/**
 * Layer 1 — Facts. The ledger (§11). He owes you $400. You own the shop on
 * 4th. He told you X on day 40. Never generated, never summarized, never
 * lossy. It is perfect because the model never touches it: everything that
 * lands here has been validated by the sim first (`sim/outcomes.ts`).
 */

import { mintFactId } from '../../sim/ids.js';
import { compareTime } from '../../sim/time.js';
import type { Actor, EventId, Fact, FactId, WorldState, WorldTime } from '../../sim/types/index.js';

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** A fact before the sim has stamped an id, a time and an origin on it. */
export type FactDraft = DistributiveOmit<Fact, 'id' | 'since' | 'origin'>;

export function writeFact(
  state: WorldState,
  draft: FactDraft,
  origin: EventId | null,
  since: WorldTime = state.time,
): { readonly state: WorldState; readonly fact: Fact } {
  const minted = mintFactId(state);
  const fact = { ...draft, id: minted.id, since, origin } as Fact;
  return {
    fact,
    state: {
      ...minted.state,
      memory: {
        ...minted.state.memory,
        facts: { ...minted.state.memory.facts, [fact.id]: fact },
      },
    },
  };
}

/** Mark an obligation settled. Returns null if there is no such open obligation. */
export function settleObligation(state: WorldState, id: FactId): WorldState | null {
  const fact = state.memory.facts[id];
  if (!fact || fact.kind !== 'obligation' || fact.settled) return null;
  return {
    ...state,
    memory: {
      ...state.memory,
      facts: { ...state.memory.facts, [id]: { ...fact, settled: true } },
    },
  };
}

/** Everyone a fact names. */
export function factParties(fact: Fact): Actor[] {
  switch (fact.kind) {
    case 'obligation':
      return [fact.from, fact.to];
    case 'ownership':
      return [fact.owner];
    case 'killing':
      return [fact.killer, fact.victim];
    case 'claim':
      return [fact.by, fact.to];
  }
}

export function factsInvolving(state: WorldState, actor: Actor): Fact[] {
  return Object.values(state.memory.facts)
    .filter((f) => factParties(f).includes(actor))
    .sort((x, y) => compareTime(y.since, x.since));
}

/** Facts that name both parties, newest first. */
export function factsBetween(state: WorldState, a: Actor, b: Actor): Fact[] {
  return factsInvolving(state, a).filter((f) => factParties(f).includes(b));
}

export function openObligationsBetween(state: WorldState, a: Actor, b: Actor): Fact[] {
  return factsBetween(state, a, b).filter((f) => f.kind === 'obligation' && !f.settled);
}
